import FlowType from "../commons/model/FlowType";
import { useEffect, useState } from "react";
import sfetch from "../commons/utils/super-fetch";
import ModalPopup from "../commons/components/modal/ModalPopup";
import * as React from "react";
import Option from "../commons/model/Option";
import Form from "../commons/components/form/Form";
import EmailInput from "../commons/components/form/input/EmailInput";
import { maxLength, required } from "../commons/model/form/Validators";
import ToggleButton from "../commons/components/buttons/ToggleButton";
import SelectInput from "../commons/components/form/input/SelectInput";
import TextInput from "../commons/components/form/input/TextInput";
import RichTextEditorInput from "../commons/components/form/input/RichTextEditorInput";
import InputState from "../commons/model/form/InputState";
import {
  EditSharingOption,
  InvitedContactInfo,
  InvitedContactInfoSummary,
  ShareReportModalProps,
} from "./ReportModels";
import { Danger, Success } from "../commons/components/Alerts";


export default function ShareReportModal({
  reportUid,
  reportType,
  hide,
  onEditSharingUpdate,
  shareAction,
  editSharingAction
} : ShareReportModalProps) {
  const [editSharingOptions, setEditSharingOptions] = useState<EditSharingOption>({
    treatmentSharedWithPatient: editSharingAction?.treatmentSharedWithPatient,
    smileVisualisationSharedWithPatient: editSharingAction?.smileVisualisationSharedWithPatient
  })
  const [isWaitingRoomUrlShared, setIsWaitingRoomUrlShared] = useState(false)
  const [showChangesSuccess, setShowChangesSuccess] = useState(false)
  const [invitedContactInfoSummary, setInvitedContactInfoSummary] = useState<InvitedContactInfoSummary>(null)
  const [showErrorMessage, setShowErrorMessage] = useState(false)

  useEffect(() => {
    Promise.resolve()
      .then(() =>
        sfetch(shareAction.urlGetInvitedContactInfos, {
          method: "GET",
        })
      )
      .then((response) =>
        response.json().then((result) => setInvitedContactInfoSummary(result))
      );
  }, []);

  const shareWithPatientForm = () => {
    const templateOptions: Option[] = shareAction.emailTemplates.map(
      (template) => new Option(template.uid, template.name)
    );
    return (
      <>
        {showChangesSuccess && (
            <Success className={"changes-saved notification-save-success-message"}>
              {i18n["Form.SendReport.ShareTreatmentWithPatient.Message.ChangesSaved"]}
            </Success>
        )}
        {showErrorMessage && (
          <Danger className={"changes-saved notification-save-error-message"}>
            {i18n["Global.TechnicalError"]}
          </Danger>
        )}
        <p className="share-form-title">
          {invitedContactInfoSummary &&
            (invitedContactInfoSummary.patient
              ? invitedContactInfoSummary.legalGuardians.length > 0
                ? i18n["Form.SendReport.ShareTreatmentWithPatient.Description.Content.PatientAndLegalGuardians"]
                : i18n["Form.SendReport.ShareTreatmentWithPatient.Description.Content.Patient"]
              : i18n["Form.SendReport.ShareTreatmentWithPatient.Description.Content.LegalGuardians"])}
        </p>
        <Form
          titleLabel=""
          submitLabel={i18n["Form.SendReport.Submit"]}
          submit={shareWithPatient.bind(this)}
          onUpdate={(changedInput, form) => {
            setShowErrorMessage(false)
            setShowChangesSuccess(false)
            if (changedInput.name === "user-email-templates") {
              let template = shareAction.emailTemplates.find(
                (tpl) => tpl.uid === changedInput.value
              );
              template && form.subject && form.subject.setValue(template.subject);
              template && form.message && form.message.setValue(template.content);
            }
          }}
        >
          {invitedContactInfoSummary && (
            <div>
              {invitedContactInfoSummary.patient && (
                <EmailInput
                  name={"patientEmail"}
                  initialValue={invitedContactInfoSummary.patient.email}
                  label={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.PatientEmail"]}
                  validators={[required()]}
                  readOnly={invitedContactInfoSummary.patient.hasAnAccount}
                />
              )}
              {invitedContactInfoSummary.legalGuardians?.map(
                (legalGuardianContactInfo, index) => (
                  <EmailInput
                    name={`legalGuardianEmail${index}`}
                    initialValue={legalGuardianContactInfo.email}
                    label={legalGuardianContactInfo.name}
                    validators={[required()]}
                    readOnly={legalGuardianContactInfo.hasAnAccount}
                  />
                )
              )}
            </div>
          )}
          {shareAction.waitingRoomUrl && (
            <>
              <div className="toggle-option">
                <p className="no-margin">
                  {
                    i18n["Form.SendReport.ShareTreatmentWithPatient.VideoConsultation.Description"]
                  }
                </p>
                <ToggleButton
                  value={isWaitingRoomUrlShared}
                  onChange={(newValue) => setIsWaitingRoomUrlShared(newValue)}
                />
              </div>
              <hr />
            </>
          )}
          <div className="message info">
            <div>
              <p>{i18n["Form.SendReport.Email.Content.Information"]}</p>
              <p>{i18n["Form.SendReport.Email.Content.SupportNotice"]}</p>
            </div>
          </div>

          <SelectInput
            options={templateOptions}
            name="user-email-templates"
            placeholder={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.SelectTemplate"]}
            label={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.Template"]}
            detail={i18n["Global.OptionalField"]}
          />
          <TextInput
            name="subject"
            placeholder={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.Subject"]}
            label={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.Subject"]}
            validators={[
              maxLength("", shareAction.subjectMaxLength),
              required()
            ]}
          />
          <RichTextEditorInput
            name="message"
            label={i18n["Form.SendReport.ShareTreatmentWithPatient.EmailTemplate.Content"]}
            className="email-content"
            cssUrl=""
            height="50rem"
            maxSize={shareAction.messageMaxLength}
            validators={[required()]}
            quickLinks={{
              bookingAppointmentUrl: shareAction.bookingAppointmentUrl,
              videoConsultationUrl: shareAction.videoConsultationUrl
            }}
          />
          {editSharingAction && reportType != FlowType.SMILE_SIMULATION && (
              <>
                <div className="toggle-option">
                  <p>{i18n["Form.SendReport.ShareTreatmentWithPatient.Toggle.Message"]}</p>
                  <ToggleButton
                    value={editSharingOptions.treatmentSharedWithPatient}
                    onChange={newValue => toggleTreatmentSharedWithPatient(newValue)}
                  />
                </div>
                {editSharingAction.hasSmileVisualisation && (
                  <div className="toggle-option">
                    <p>{i18n["Form.SendReport.ShareSmileVisualisationWithPatient.Toggle.Message"]}</p>
                    <ToggleButton
                      value={editSharingOptions.smileVisualisationSharedWithPatient}
                      onChange={newValue => toggleSmileVisualisationSharedWithPatient(newValue)}
                    />
                  </div>
                )}
              </>
            )}
          <div className="mobile-bottom-spacer" />
        </Form>
      </>
    );
  }

  const updateEmail = (inputState: InputState, invitedContactInfo: InvitedContactInfo): Promise<Response> => {
    if (invitedContactInfo.email !== inputState.value)
      return sfetch(shareAction.urlUpdateEmail, {
        method: "POST",
        body: JSON.stringify({
          email: inputState.value,
          patientUid: invitedContactInfo.uid,
        }),
        headers: { "Content-Type": "application/json" },
      });
    }

  const shareWithPatient = (form: { [key: string]: InputState }) => {
    setShowErrorMessage(false)
    setShowChangesSuccess(false)
    const waitingRoomUrl = isWaitingRoomUrlShared && shareAction.waitingRoomUrl != null
        ? shareAction.waitingRoomUrl
        : null;
    return Promise.resolve()
      .then(
        () =>
          invitedContactInfoSummary.patient &&
          updateEmail(form["patientEmail"], invitedContactInfoSummary.patient)
      )
      .then(() =>
        Promise.all(
          invitedContactInfoSummary.legalGuardians.map(
            (invitedContactInfo, index) =>
              updateEmail(form[`legalGuardianEmail${index}`], invitedContactInfoSummary.legalGuardians[index])
          )
        )
      )
      .then(() =>
        sfetch(shareAction.urlShare, {
          method: "POST",
          body: JSON.stringify({
            reportUid: reportUid,
            subject: form.subject.value,
            message: form.message.value,
            waitingRoomUrl: waitingRoomUrl,
          }),
          headers: { "Content-Type": "application/json" },
        })
      )
      .then(hide)
      .catch(() => {
        setShowErrorMessage(true)
      });
  }

  const toggleTreatmentSharedWithPatient = (newValue: boolean) => {
    toggleEditSharingOption(
      newValue,
      editSharingOptions.smileVisualisationSharedWithPatient,
      JSON.stringify({ treatmentSharedWithPatient: newValue })
    )
  }

  const toggleSmileVisualisationSharedWithPatient = (newValue: boolean) => {
    toggleEditSharingOption(
      editSharingOptions.treatmentSharedWithPatient,
      newValue,
      JSON.stringify({ smileVisualisationSharedWithPatient: newValue })
    )
  }

  const toggleEditSharingOption = (treatmentSharedWithPatient: boolean, smileVisualisationSharedWithPatient: boolean, body: any) => {
    setShowChangesSuccess(false)
    const updatedEditSharingOption = {
      treatmentSharedWithPatient: treatmentSharedWithPatient,
      smileVisualisationSharedWithPatient: smileVisualisationSharedWithPatient
    }
    sfetch(editSharingAction.urlEditSharing, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: body,
    }).then(() => {
      onEditSharingUpdate(updatedEditSharingOption);
      setEditSharingOptions(updatedEditSharingOption);
      setShowChangesSuccess(true)
    });
  }

  return (
    <ModalPopup
      closeModal={hide}
      id="modal-share"
      class="share-form"
      fullscreen={true}
    >
      <div className="title">{i18n["Form.SendReport.Title"]}</div>
      {shareWithPatientForm()}
    </ModalPopup>
  );
}